import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { Chip } from 'src/components/shared/Chip'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import { MAX_VISIBLE_STEP_CHIPS } from 'src/constants/onboarding-template/constants'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'
import { getTemplateSteps } from 'src/utils/onboardingTemplateList'

interface Props {
  template: OnboardingTemplate
}

const OnboardingTemplateStepsCellComponent = ({ template }: Props) => {
  // Duplicates are deliberately not deduped: two `form` steps are two things the contractor does.
  const stepLabels = useMemo(() => {
    return getTemplateSteps(template).map((step) => {
      return stepLabel(step.type)
    })
  }, [template])

  if (stepLabels.length === 0) {
    // A dash for a genuinely empty list is a cosmetic default, not fabricated content. A
    // template with no step-groups really does collect nothing through this endpoint — see the
    // design doc's §2.2.
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        —
      </Typography>
    )
  }

  const visibleLabels = stepLabels.slice(0, MAX_VISIBLE_STEP_CHIPS)
  const hiddenCount = stepLabels.length - visibleLabels.length

  return (
    <Tooltip title={stepLabels.join(' · ')}>
      <div css={tw`flex min-w-0 flex-row items-center gap-1 overflow-hidden`}>
        {visibleLabels.map((label, index) => {
          return (
            <Chip
              // Labels repeat within a template, so the index is part of the key.
              key={`${label}-${index}`}
              text={label}
              className={tw`flex-shrink-0`}
            />
          )
        })}
        {hiddenCount > 0 && (
          <Typography variant="body2" css={tw`flex-shrink-0 text-shades-500`}>
            {`+${hiddenCount}`}
          </Typography>
        )}
      </div>
    </Tooltip>
  )
}

export const OnboardingTemplateStepsCell = memo(OnboardingTemplateStepsCellComponent)
